import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { Route } from 'react-router-dom';
import { Redirect, Switch, useLocation, useRouteMatch } from 'react-router';
import { AppState } from '../../store/appState';
import {
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EMAGAZINES_OBJECT,
    LOANFORMAT_EPRESS_OBJECT,
    URL_LOANFORMAT_ALL,
    URL_LOANFORMAT_EAUDIOBOOKS,
    URL_LOANFORMAT_EBOOKS,
    URL_LOANFORMAT_EPRESS
} from '../../store/loanFormat/actions';
import { PageNotFound } from '../../shop/PageNotFound';
import { CollectionByGenrePage } from './by-genre/CollectionByGenrePage';
import { CompleteCollectionPage } from './complete/CompleteCollectionPage';
import { CollectionNewlyAddedPage } from './newly-added/CollectionNewlyAddedPage';
import { CollectionSearchResultPage } from '../../shared/search/CollectionSearchResultPage';
import { CheckLoanFormat } from '../../shared/components/CheckLoanFormat';
import { COLLECTION_NAVIGATION_V2, useFeature } from '../../shared/utils/togglz';
import { ProductSelectionProvider } from '../../shared/product-selection/ProductSelectionContext';

const RouteSwitch: FC = () => {
    const { path, url } = useRouteMatch();
    const { pathname, search } = useLocation();
    const currentLoanFormat = useSelector((state: AppState) => {
        switch (state.loanFormat.key) {
            case LOANFORMAT_EBOOK_OBJECT.key:
                return URL_LOANFORMAT_EBOOKS;
            case LOANFORMAT_EAUDIOBOOK_OBJECT.key:
                return URL_LOANFORMAT_EAUDIOBOOKS;
            case LOANFORMAT_EPRESS_OBJECT.key:
            case LOANFORMAT_EMAGAZINES_OBJECT.key:
                return URL_LOANFORMAT_EPRESS;
            default:
                return URL_LOANFORMAT_ALL;
        }
    });
    const isCollectionNavigationV2Enabled = useFeature(COLLECTION_NAVIGATION_V2);

    return (
        <Switch>
            <Route exact path={path}>
                <Redirect to={{ pathname: `${url}/complete`, search }} />
            </Route>
            <Route exact path={`${path}/complete`}>
                <Redirect to={{ pathname: `${url}/complete/${currentLoanFormat}`, search }} />
            </Route>

            <Route exact path={`${path}/complete/:loanFormat`}>
                {isCollectionNavigationV2Enabled ? (
                    <Redirect to={{ pathname: pathname.replace('/collection', '/collectionmgmt'), search }} />
                ) : (
                    <CheckLoanFormat>
                        <ProductSelectionProvider>
                            <CompleteCollectionPage />
                        </ProductSelectionProvider>
                    </CheckLoanFormat>
                )}
            </Route>
            <Route exact path={`${path}/search/:loanFormat`}>
                {isCollectionNavigationV2Enabled ? (
                    <Redirect to={{ pathname: pathname.replace('/collection', '/collectionmgmt'), search }} />
                ) : (
                    <CheckLoanFormat>
                        <ProductSelectionProvider>
                            <CollectionSearchResultPage />
                        </ProductSelectionProvider>
                    </CheckLoanFormat>
                )}
            </Route>
            <Route exact path={`${path}/newly-added`}>
                <Redirect to={{ pathname: `${url}/newly-added/${currentLoanFormat}`, search }} />
            </Route>
            <Route path={`${path}/newly-added/:loanFormat`}>
                {isCollectionNavigationV2Enabled ? (
                    <Redirect
                        to={{
                            pathname: pathname.replace('/collection/newly-added', '/collectionmgmt/collection'),
                            search
                        }}
                    />
                ) : (
                    <CheckLoanFormat>
                        <ProductSelectionProvider>
                            <CollectionNewlyAddedPage />
                        </ProductSelectionProvider>
                    </CheckLoanFormat>
                )}
            </Route>
            <Route exact path={`${path}/by-genre`}>
                <Redirect to={{ pathname: `${url}/by-genre/all`, search }} />
            </Route>
            <Route path={`${path}/by-genre/:loanFormat/:genre?/:subgenre?`}>
                {isCollectionNavigationV2Enabled ? (
                    <Redirect
                        to={{
                            pathname: pathname.replace('/collection/by-genre', '/collectionmgmt/collection'),
                            search
                        }}
                    />
                ) : (
                    <CheckLoanFormat>
                        <ProductSelectionProvider>
                            <CollectionByGenrePage />
                        </ProductSelectionProvider>
                    </CheckLoanFormat>
                )}
            </Route>
            <Route>
                <PageNotFound />
            </Route>
        </Switch>
    );
};

export default RouteSwitch;
